import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { AuthLayout } from "../../../shared/components/layout/AuthLayout";
import SignUpIllustration from "../../../assets/illustrations/signup.svg";
import {
  VALIDATOR_REQUIRE,
  VALIDATOR_EMAIL,
  VALIDATOR_MINLENGTH,
} from "../../../shared/utils/validators";
import PasswordField from "../../../shared/components/ui/PasswordField";
import FormButton from "../../../shared/components/ui/FormButton";
import { getPublicClients } from "../../../core/api/clientsService";

const getClientId = (client) => client?.id ?? client?.client_id;

const getClientName = (client) =>
  client?.organization_name ||
  client?.organizationName ||
  client?.org_name ||
  client?.orgName ||
  client?.client_name ||
  client?.clientName ||
  client?.company_name ||
  client?.companyName ||
  client?.name ||
  "";

const normalizeClientOption = (client) => {
  const id = getClientId(client);
  const name = getClientName(client);

  if (!id || !name) {
    return null;
  }

  return {
    id: String(id),
    name: String(name),
  };
};

const SignUpView = ({
  formState,
  inputHandler,
  handleTextInputChange,
  handleSignUp,
  loginState,
  setLoginState,
  isActionLoading,
  textFieldStyle,
}) => {
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchClients = async () => {
      setIsLoadingClients(true);
      setClientsError("");

      const response = await getPublicClients();

      if (!isMounted) {
        return;
      }

      if (response?.error) {
        setClients([]);
        setClientsError(response.error.message || "Unable to load organizations.");
        setIsLoadingClients(false);
        return;
      }

      const options = (response?.clients || [])
        .map(normalizeClientOption)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));

      setClients(options);
      setClientsError(options.length ? "" : "No organizations are available for signup.");
      setIsLoadingClients(false);
    };

    fetchClients();

    return () => {
      isMounted = false;    
    };
  }, []);

  const selectedClientId = formState.inputs.client_id?.value || "";
  const isClientSelectDisabled = isLoadingClients || clients.length === 0;
  const clientHelperText = useMemo(() => {
    if (isLoadingClients) return "Loading organizations...";
    if (clientsError) return clientsError;
    return "Select the organization this account belongs to.";
  }, [clientsError, isLoadingClients]);

  return (
    <AuthLayout
      title="Sign Up"
      subtitle="Let's get you all set up so you can access your personal account."
      illustration={SignUpIllustration}
    >
      <Box component="form" onSubmit={handleSignUp} className="flex flex-col gap-4 sm:gap-6 md:gap-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TextField
            label="First Name"
            name="firstname"
            value={formState.inputs.firstname.value}
            onChange={(e) =>
              handleTextInputChange(e, inputHandler, [VALIDATOR_REQUIRE()])
            }
            variant="outlined"
            placeholder="Your First Name"
            required
            fullWidth
            sx={textFieldStyle}
            focused
          />
          <TextField
            label="Last Name"
            name="lastname"
            value={formState.inputs.lastname.value}
            onChange={(e) =>
              handleTextInputChange(e, inputHandler, [VALIDATOR_REQUIRE()])
            }
            variant="outlined"
            placeholder="Your Last Name"
            required
            fullWidth
            sx={textFieldStyle}
            focused
          />
        </div>

        <TextField
          label="Email"
          name="email"
          type="email"
          value={formState.inputs.email.value}
          onChange={(e) =>
            handleTextInputChange(e, inputHandler, [
              VALIDATOR_REQUIRE(),
              VALIDATOR_EMAIL(),
            ])
          }
          placeholder="Enter Your Email"
          variant="outlined"
          fullWidth
          required
          sx={textFieldStyle}
          focused
        />

        <TextField
          select
          label="Organization"
          name="client_id"
          value={selectedClientId}
          onChange={(e) =>
            handleTextInputChange(e, inputHandler, [VALIDATOR_REQUIRE()])
          }
          variant="outlined"
          fullWidth
          required
          disabled={isClientSelectDisabled}
          error={Boolean(clientsError)}
          helperText={clientHelperText}
          sx={textFieldStyle}
          focused
          InputProps={{
            endAdornment: isLoadingClients ? (
              <CircularProgress color="inherit" size={18} />
            ) : null,
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (!value) {
                return <span className="text-gray-400">Select your organization</span>;
              }

              return clients.find((client) => client.id === String(value))?.name || "";
            },
          }}
        >
          <MenuItem value="" disabled>
            Select your organization
          </MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.id} value={client.id}>
              {client.name}
            </MenuItem>
          ))}
        </TextField>

        <PasswordField
          name="password"
          formState={formState}
          handler={inputHandler}
          validators={[VALIDATOR_REQUIRE(), VALIDATOR_MINLENGTH(8)]}
          handleTextInputChange={handleTextInputChange}
          placeholder="Enter Your Password"
          variant="outlined"
          fullWidth
          required
          sx={textFieldStyle}
          label="Enter Your Password"
          focused
        />

        <PasswordField
          key="confirm_password"
          label="Confirm Password"
          name="confirm_password"
          formState={formState}
          handler={inputHandler}
          validators={[VALIDATOR_REQUIRE(), VALIDATOR_MINLENGTH(8)]}
          handleTextInputChange={handleTextInputChange}
          placeholder="Confirm Your Password"
          variant="outlined"
          fullWidth
          required
          sx={textFieldStyle}
          focused
        />
        <div className="flex justify-between items-center mb-6">
          <FormControlLabel
            control={<Checkbox required />}
            label={
              <span className="text-primary text-sm">
                I agree to all the <span className="font-bold">Terms </span>
                and<span className="font-bold"> Privacy Policies</span>
              </span>
            }
          />
        </div>

        <FormButton
          type="submit"
          variant="contained"
          fullWidth
          size="medium"
          sx={{ backgroundColor: "primary.main" }}
          loading={isActionLoading}
          disabled={isClientSelectDisabled}
        >
          Create Account
        </FormButton>
      </Box>

      <div className="text-center mt-8">
        <span className="text-sm">Already have an account? </span>
        <span
          onClick={() => setLoginState({ state: "login", message: null })}
          className="text-primary font-bold hover:underline cursor-pointer"
        >
          Login
        </span>
      </div>
    </AuthLayout>
  );
};

export default SignUpView;
